import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../lib/cn';
import type { CurrencyCode } from '../mocks/clientBatch';
import { useCurrencies } from '../api/platform';

export type CurrencySelectorProps = {
  value: CurrencyCode;
  onChange: (code: CurrencyCode) => void;
  size?: 'sm' | 'md';
};

/**
 * Compact pill-style currency selector.
 *
 *   [ ৳ BDT ▾ ]
 *
 * Reads the active list from the currencies seam (useCurrencies).
 * On click, opens a dropdown listing each active currency:
 *   {flag} {code} {symbol} — {name}
 */
export function CurrencySelector({
  value,
  onChange,
  size = 'md',
}: CurrencySelectorProps) {
  const { data: currencies = [] } = useCurrencies();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const active = currencies.filter((c) => c.isActive);
  const current = active.find((c) => c.code === value) ?? active[0];

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const sizeCls =
    size === 'sm'
      ? 'px-2.5 py-1 text-xs gap-1.5'
      : 'px-3 py-1.5 text-sm gap-2';

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center font-medium rounded-full transition',
          'bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50',
          'dark:bg-zinc-900 dark:text-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
          'dark:focus-visible:ring-brand-dm dark:focus-visible:ring-offset-zinc-950',
          sizeCls,
        )}
      >
        <span className="tabular-nums">{current?.symbol}</span>
        <span>{current?.code}</span>
        <ChevronDown
          className={cn(
            size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5',
            'text-zinc-500 dark:text-zinc-400 transition',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label="Choose currency"
          className={cn(
            'absolute right-0 z-30 mt-1.5 min-w-[15rem] py-1.5',
            'rounded-lg border bg-white shadow-lg',
            'dark:bg-zinc-900 dark:border-zinc-700',
            'border-zinc-200',
          )}
        >
          {active.map((c) => {
            const selected = c.code === value;
            return (
              <li key={c.code} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(c.code);
                    setOpen(false);
                  }}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2 text-sm transition',
                    'hover:bg-zinc-50 dark:hover:bg-zinc-800/70',
                    selected &&
                      'bg-brand/5 text-brand dark:bg-brand-dm/10 dark:text-brand-dm',
                  )}
                >
                  <span className="text-base leading-none" aria-hidden="true">
                    {c.flag}
                  </span>
                  <span className="font-semibold">{c.code}</span>
                  <span className="text-zinc-500 dark:text-zinc-400">
                    {c.symbol}
                  </span>
                  <span className="text-zinc-500 dark:text-zinc-400 truncate">
                    — {c.name}
                  </span>
                  {selected && (
                    <Check className="w-3.5 h-3.5 ml-auto text-brand dark:text-brand-dm" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
